import Icon from '@/components/ui/AppIcon';

interface Portal {
  id: string;
  title: string;
  description: string;
  icon: string;
  features: string[];
  accessLevel: string;
  bgColor: string;
  iconColor: string;
}

interface StakeholderPortalsProps {
  onPortalSelect: (portalId: string) => void;
  className?: string;
}

const StakeholderPortals = ({ onPortalSelect, className = '' }: StakeholderPortalsProps) => {
  const portals: Portal[] = [
    {
      id: 'institutional-investors',
      title: 'Institutional Investors',
      description: 'Development finance institutions, impact investors, and pension funds seeking long-term African exposure with measurable returns.',
      icon: 'BuildingLibraryIcon',
      features: [
        'Investment deck and financial projections',
        'Due diligence documentation',
        'Quarterly performance reports',
        'Risk assessment frameworks',
        'Investment committee materials'
      ],
      accessLevel: 'Tier 1 - Secure Access',
      bgColor: 'bg-primary/5',
      iconColor: 'text-primary'
    },
    {
      id: 'government-partners',
      title: 'Government Partners',
      description: 'Kenyan and regional government officials evaluating public-private partnerships for economic development and job creation.',
      icon: 'BuildingOffice2Icon',
      features: [
        'Partnership framework documentation',
        'Regulatory compliance reports',
        'Economic impact assessments',
        'Regional development strategies',
        'Government engagement protocols'
      ],
      accessLevel: 'Tier 1 - Secure Access',
      bgColor: 'bg-secondary/5',
      iconColor: 'text-secondary'
    },
    {
      id: 'strategic-collaborators',
      title: 'Strategic Collaborators',
      description: 'Multinational corporations, NGOs, and technical partners seeking authentic African development partnerships.',
      icon: 'UserGroupIcon',
      features: [
        'Collaboration framework overview',
        'Sector-specific opportunities',
        'Partnership case studies',
        'Technical integration guidelines',
        'Joint venture structures'
      ],
      accessLevel: 'Tier 2 - Standard Access',
      bgColor: 'bg-accent/5',
      iconColor: 'text-brand-earth'
    },
    {
      id: 'industry-observers',
      title: 'Industry Observers',
      description: 'Development economists, policy researchers, and media covering innovative approaches to African economic transformation.',
      icon: 'AcademicCapIcon',
      features: [
        'Research reports and white papers',
        'Impact measurement data',
        'Thought leadership content',
        'Media resources and press kits',
        'Public presentations and webinars'
      ],
      accessLevel: 'Tier 3 - Public Access',
      bgColor: 'bg-brand-forest/5',
      iconColor: 'text-brand-forest'
    }
  ];

  return (
    <section className={`py-16 bg-background ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="font-headline text-3xl md:text-4xl font-semibold text-foreground mb-4">
            Stakeholder-Specific Portals
          </h2>
          <p className="font-body text-lg text-text-secondary max-w-3xl mx-auto">
            Tailored engagement pathways designed for your specific partnership needs and objectives.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {portals.map((portal) => (
            <div
              key={portal.id}
              className={`${portal.bgColor} border border-border rounded-lg p-8 hover:shadow-institutional transition-all duration-300`}
            >
              <div className="flex items-start space-x-4 mb-6">
                <div className={`flex items-center justify-center w-14 h-14 ${portal.bgColor} rounded-lg`}>
                  <Icon name={portal.icon as any} size={28} variant="outline" className={portal.iconColor} />
                </div>
                <div className="flex-1">
                  <h3 className="font-headline text-xl font-semibold text-foreground mb-2">
                    {portal.title}
                  </h3>
                  <span className="inline-block px-3 py-1 bg-muted text-text-secondary text-xs font-body font-medium rounded-full">
                    {portal.accessLevel}
                  </span>
                </div>
              </div>

              <p className="font-body text-sm text-text-secondary leading-relaxed mb-6">
                {portal.description}
              </p>

              <div className="mb-6">
                <h4 className="font-body text-sm font-semibold text-foreground mb-3">
                  Available Resources:
                </h4>
                <ul className="space-y-2">
                  {portal.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Icon name="CheckCircleIcon" size={16} variant="solid" className={`${portal.iconColor} mt-0.5 flex-shrink-0`} />
                      <span className="font-body text-sm text-text-secondary">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                onClick={() => onPortalSelect(portal.id)}
                className="w-full px-6 py-3 bg-secondary text-secondary-foreground font-body font-medium text-sm rounded-md hover:bg-secondary/90 hover:shadow-md transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <span>Access Portal</span>
                <Icon name="ArrowRightIcon" size={16} variant="outline" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StakeholderPortals;